/*
 * La Sura collection filters: the desktop "Show filters" toggle (Matt, 2026-09-28, after
 * Powerlete's collection page, whose powerlete-collection-filters.js this is ported from).
 *
 * Horizon's own vertical filters draw the side pane and do all the filtering (AJAX, URL,
 * facet form). This element only opens and closes the pane, with a `data-open` attribute on
 * itself; lasura-collection-filters.css reads it through `results-list:has(...)`, pushes the
 * grid across and the cards get narrower. Phones and anything under 750px keep the drawer.
 *
 * The first state is rendered by Liquid (open when the page arrives with filters applied),
 * so the first paint is already right and nothing moves as the script loads.
 *
 * Applying a filter or a sort morphs the section, which resets this element's attributes to
 * the server's. connectedCallback doesn't run again, so:
 *   1. the click is delegated to this element, which survives the button being replaced;
 *   2. a MutationObserver puts back the shopper's choice (open or closed) after each morph,
 *      before paint, so the pane doesn't flicker or change its mind.
 */

const KEY = 'lasuraFiltersOpen';

/* sessionStorage throws in some private modes; the pane then just doesn't remember. */
const read = () => {
  try {
    return sessionStorage.getItem(KEY);
  } catch (error) {
    return null;
  }
};
const write = (value) => {
  try {
    sessionStorage.setItem(KEY, value);
  } catch (error) {
    /* Storage blocked. */
  }
};

class LasuraCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.mq = window.matchMedia('(min-width: 750px)');

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onMedia = this.onMedia.bind(this);
    this.reassert = this.reassert.bind(this);

    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    this.mq.addEventListener('change', this.onMedia);

    this.observer = new MutationObserver(this.reassert);
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-open'] });

    write(this.isOpen ? '1' : '0');
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.mq?.removeEventListener('change', this.onMedia);
    this.observer?.disconnect();
  }

  get button() {
    return this.querySelector('.lasura-filters__toggle');
  }

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  onClick(event) {
    if (!event.target.closest('.lasura-filters__toggle')) return;
    this.setOpen(!this.isOpen);
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen || !this.mq.matches) return;
    /* Escape inside an open filter's panel closes that panel first (Horizon's own). */
    if (event.target.closest?.('details[open]')) return;
    this.setOpen(false);
    this.button?.focus();
  }

  onMedia(event) {
    /* Under 750px the drawer takes over; start closed if the window widens again. */
    if (!event.matches && this.isOpen) this.setOpen(false);
  }

  reassert() {
    const wanted = read() === '1';
    if (wanted !== this.isOpen) this.toggleAttribute('data-open', wanted);
    this.sync();
  }

  sync() {
    const button = this.button;
    if (!button) return;
    const open = this.isOpen;
    button.setAttribute('aria-expanded', String(open));
    const label = button.querySelector('.lasura-filters__label');
    const text = open ? this.dataset.labelHide : this.dataset.labelShow;
    if (label && text && label.textContent !== text) label.textContent = text;
  }

  setOpen(open) {
    write(open ? '1' : '0');
    this.toggleAttribute('data-open', open);
  }
}

if (!customElements.get('lasura-collection-filters')) {
  customElements.define('lasura-collection-filters', LasuraCollectionFilters);
}
